import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Rocket, Target, Users, BarChart3, Plus, Check, Send, 
  CircleDollarSign, Sparkles, CheckCircle2, Crown, Settings,
  ArrowRight, Clock, FileText, CreditCard, Wallet, Eye,
  AlertCircle, ChevronRight, TrendingUp, Star, Globe, Share2,
  Link2, ArrowLeft, Zap, RefreshCw, Copy, ExternalLink, Hash
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useApp } from "../contexts/AppContext";
import { 
  CAMPAIGN_GOALS, QUEST_TYPE_CONFIG, CAMPAIGN_PRICING, 
  WEB2_MARKETING_COPY, CAMPAIGN_BUILDER_STEPS 
} from "../constants/quests";
import { campaignsAPI } from "../services/api";
import { API } from "../constants";

// Partner payment wallet address
const PARTNER_PAYMENT_WALLET = "addr1qxc6u2d5ppeuhg7tkj5quf3csxp2022g98az65hpad9m6yreuxes6p3hhg880tegsg95xqwek6srgac5jq04x9rcm2qq6a6md7";

// Campaign Studio - Web2-Friendly Partner Portal
export const CampaignStudioPage = () => {
  const { user } = useApp();
  const [activeView, setActiveView] = useState("landing"); // landing, builder, dashboard
  const [builderStep, setBuilderStep] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('ada');
  const [submitting, setSubmitting] = useState(false);
  
  // Campaign builder state
  const [campaign, setCampaign] = useState({
    goal: null,
    questType: null,
    budget: null,
    title: '',
    description: '',
    targetUrl: '',
  });

  // Check partner status from localStorage (mock)
  const [partnerStatus, setPartnerStatus] = useState(() => {
    const stored = localStorage.getItem('burnz_partner_status');
    return stored ? JSON.parse(stored) : null;
  });

  const isActivatedPartner = partnerStatus?.status === 'active';

  // Handle campaign creation
  const handleCreateCampaign = async () => {
    setSubmitting(true);
    
    try {
      // Create campaign via API
      const tier = CAMPAIGN_PRICING[campaign.budget?.toUpperCase()] || CAMPAIGN_PRICING.STARTER;
      
      const campaignData = {
        title: campaign.title,
        description: campaign.description,
        campaign_type: campaign.questType,
        goal: campaign.goal,
        target_url: campaign.targetUrl,
        budget_ada: tier.price_ada,
        budget_burnz: tier.price_burnz,
        payment_method: paymentMethod,
        reach: tier.reach,
        duration: tier.duration,
        status: 'active',
      };

      const result = await campaignsAPI.create(campaignData);
      
      // Also store locally for immediate UI update
      const partnerData = {
        status: 'active',
        tier: campaign.budget,
        campaigns: [{ ...campaignData, id: result.campaign_id || `c_${Date.now()}`, created_at: new Date().toISOString() }],
        activated_at: new Date().toISOString(),
      };
      
      localStorage.setItem('burnz_partner_status', JSON.stringify(partnerData));
      setPartnerStatus(partnerData);
      setShowPaymentModal(false);
      setActiveView('dashboard');
      
    } catch (error) {
      // Fallback to local storage on API failure
      const partnerData = {
        status: 'active',
        tier: campaign.budget,
        campaigns: [{ ...campaign, id: `c_${Date.now()}`, status: 'active', created_at: new Date().toISOString() }],
        activated_at: new Date().toISOString(),
      };
      
      localStorage.setItem('burnz_partner_status', JSON.stringify(partnerData));
      setPartnerStatus(partnerData);
      setShowPaymentModal(false);
      setActiveView('dashboard');
    }
    
    setSubmitting(false);
  };

  // If activated partner, show dashboard
  if (isActivatedPartner) {
    return <PartnerDashboard partnerStatus={partnerStatus} onCreateNew={() => { setActiveView('builder'); setBuilderStep(1); setCampaign({ goal: null, questType: null, budget: null, title: '', description: '', targetUrl: '' }); }} />;
  }

  // Builder View
  if (activeView === 'builder') {
    return (
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            className="text-white/50 hover:text-white"
            onClick={() => { setActiveView('landing'); setBuilderStep(1); }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Create Your Campaign</h1>
            <p className="text-white/50 text-sm">Step {builderStep} of 4</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {CAMPAIGN_BUILDER_STEPS.map((step, i) => (
            <div key={step.step} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                builderStep >= step.step ? 'bg-purple-500 text-white' : 'bg-white/10 text-white/50'
              }`}>
                {builderStep > step.step ? <Check className="w-4 h-4" /> : step.step}
              </div>
              <div className={`flex-1 h-1 mx-2 rounded ${
                builderStep > step.step ? 'bg-purple-500' : 'bg-white/10'
              }`} />
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="bg-[#1A1A1A] border-white/5">
          <CardContent className="p-6">
            {/* Step 1: Choose Goal */}
            {builderStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">What do you want to achieve?</h2>
                  <p className="text-white/50">Select your primary campaign goal</p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {Object.values(CAMPAIGN_GOALS).map(goal => (
                    <button
                      key={goal.id}
                      onClick={() => setCampaign({ ...campaign, goal: goal.id })}
                      className={`p-4 rounded-xl border text-center transition-all ${
                        campaign.goal === goal.id 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                      data-testid={`goal-${goal.id}`}
                    >
                      <span className="text-3xl mb-2 block">{goal.icon}</span>
                      <p className="text-white font-medium">{goal.label}</p>
                      <p className="text-white/40 text-xs mt-1">{goal.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Choose Action Type */}
            {builderStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">What should users do?</h2>
                  <p className="text-white/50">Select the type of action for your campaign</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(QUEST_TYPE_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setCampaign({ ...campaign, questType: key })}
                      className={`p-4 rounded-xl border text-left transition-all ${
                        campaign.questType === key 
                          ? `border-${config.color}-500 bg-${config.color}-500/10` 
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                      data-testid={`type-${key}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-2xl">{config.icon}</span>
                        <div>
                          <p className="text-white font-medium">{config.label}</p>
                          <p className={`text-${config.color}-400 text-xs`}>{config.description}</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {config.examples.slice(0, 3).map((ex, i) => (
                          <p key={i} className="text-white/40 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-400" />{ex}
                          </p>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Set Budget */}
            {builderStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">Set Your Budget</h2>
                  <p className="text-white/50">Choose a plan that fits your needs</p>
                </div>
                
                <div className="grid grid-cols-3 gap-6">
                  {Object.values(CAMPAIGN_PRICING).map(tier => (
                    <button
                      key={tier.id}
                      onClick={() => setCampaign({ ...campaign, budget: tier.id })}
                      className={`p-6 rounded-xl border text-left transition-all relative ${
                        campaign.budget === tier.id 
                          ? 'border-purple-500 bg-purple-500/10' 
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                      data-testid={`budget-${tier.id}`}
                    >
                      {tier.popular && (
                        <Badge className="absolute top-3 right-3 bg-orange-500 text-white border-0 text-xs">
                          POPULAR
                        </Badge>
                      )}
                      <h3 className="text-white font-bold text-lg mb-1">{tier.name}</h3>
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="text-3xl font-bold text-white">{tier.price_ada}</span>
                        <span className="text-white/50">ADA</span>
                      </div>
                      <p className="text-white/30 text-sm mb-4">
                        or {(tier.price_burnz / 1000).toFixed(0)}K BURNZ (save up to 30%)
                      </p>
                      <div className="space-y-2 mb-4">
                        <p className="text-white/70 text-sm flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-400" />
                          {tier.reach} reach
                        </p>
                        <p className="text-white/70 text-sm flex items-center gap-2">
                          <Clock className="w-4 h-4 text-purple-400" />
                          {tier.duration}
                        </p>
                        <p className="text-white/70 text-sm flex items-center gap-2">
                          <Target className="w-4 h-4 text-orange-400" />
                          Up to {tier.quests_included} campaigns
                        </p>
                      </div>
                      <ul className="space-y-1">
                        {tier.features.map((f, i) => (
                          <li key={i} className="text-white/50 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-400" />{f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Review & Launch */}
            {builderStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">Campaign Details</h2>
                  <p className="text-white/50">Fill in the details and launch your campaign</p>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-white/70 text-sm block mb-2">Campaign Title *</label>
                      <Input 
                        placeholder="e.g., Share Our Product Launch"
                        value={campaign.title}
                        onChange={(e) => setCampaign({ ...campaign, title: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="campaign-title"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm block mb-2">Description *</label>
                      <Textarea 
                        placeholder="What do you want users to do?"
                        value={campaign.description}
                        onChange={(e) => setCampaign({ ...campaign, description: e.target.value })}
                        className="bg-white/5 border-white/10 text-white min-h-[100px]"
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm block mb-2">Target URL (optional)</label>
                      <Input 
                        placeholder="https://yoursite.com/page"
                        value={campaign.targetUrl}
                        onChange={(e) => setCampaign({ ...campaign, targetUrl: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  {/* Summary */}
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <h3 className="text-white font-bold mb-4">Campaign Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/50">Goal</span>
                        <span className="text-white">{CAMPAIGN_GOALS[campaign.goal?.toUpperCase()]?.label || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Action Type</span>
                        <span className="text-white">{QUEST_TYPE_CONFIG[campaign.questType]?.label || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/50">Plan</span>
                        <span className="text-white">{CAMPAIGN_PRICING[campaign.budget?.toUpperCase()]?.name || '-'}</span>
                      </div>
                      <div className="border-t border-white/10 pt-3 mt-3">
                        <div className="flex justify-between">
                          <span className="text-white/50">Total</span>
                          <span className="text-white font-bold text-lg">
                            {CAMPAIGN_PRICING[campaign.budget?.toUpperCase()]?.price_ada || 0} ADA
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-6 border-t border-white/10 mt-6">
              {builderStep > 1 && (
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white"
                  onClick={() => setBuilderStep(builderStep - 1)}
                >
                  Back
                </Button>
              )}
              <div className="flex-1" />
              {builderStep < 4 ? (
                <Button 
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  onClick={() => setBuilderStep(builderStep + 1)}
                  disabled={
                    (builderStep === 1 && !campaign.goal) ||
                    (builderStep === 2 && !campaign.questType) ||
                    (builderStep === 3 && !campaign.budget)
                  }
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  className="bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => setShowPaymentModal(true)}
                  disabled={!campaign.title || !campaign.description}
                  data-testid="launch-campaign-btn"
                >
                  <Rocket className="w-4 h-4 mr-2" />Launch Campaign
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Modal */}
        <PaymentModal 
          open={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          campaign={campaign}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          onPay={handleCreateCampaign}
          submitting={submitting}
        />
      </div>
    );
  }

  // Landing View (Default)
  return (
    <div className="space-y-8">
      {/* Hero Section - Web2 Friendly */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-purple-900/50 via-[#1A1A1A] to-orange-900/30 border border-white/10">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200')] opacity-5 bg-cover bg-center" />
        <div className="relative p-8 md:p-12">
          <Badge className="bg-purple-500/20 text-purple-400 border-0 mb-4">CAMPAIGN STUDIO</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 max-w-2xl">
            {WEB2_MARKETING_COPY.headline}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mb-8">
            {WEB2_MARKETING_COPY.subheadline}
          </p>
          <div className="flex items-center gap-4">
            <Button 
              size="lg" 
              className="bg-purple-500 hover:bg-purple-600 text-white h-12 px-8"
              onClick={() => setActiveView('builder')}
              data-testid="start-campaign-btn"
            >
              <Rocket className="w-5 h-5 mr-2" />{WEB2_MARKETING_COPY.cta}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white h-12 px-8"
            >
              <Eye className="w-5 h-5 mr-2" />See Examples
            </Button>
          </div>
        </div>
      </div>

      {/* Benefits Grid */}
      <div className="grid grid-cols-4 gap-4">
        {WEB2_MARKETING_COPY.benefits.map((benefit, i) => (
          <Card key={i} className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-6 text-center">
              <span className="text-3xl mb-3 block">{benefit.icon}</span>
              <h3 className="text-white font-bold mb-1">{benefit.title}</h3>
              <p className="text-white/50 text-sm">{benefit.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "Active Users", value: "45K+", icon: Users, color: "purple" },
          { label: "Campaigns Completed", value: "2.3K", icon: CheckCircle2, color: "green" },
          { label: "Avg. Engagement", value: "78%", icon: TrendingUp, color: "orange" },
          { label: "Client Satisfaction", value: "4.9/5", icon: Star, color: "yellow" }
        ].map((stat, i) => (
          <Card key={i} className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-4 text-center">
              <stat.icon className={`w-6 h-6 text-${stat.color}-400 mx-auto mb-2`} />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-white/50 text-xs">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* How It Works - 4 Steps */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white">Launch in 4 Simple Steps</CardTitle>
          <CardDescription className="text-white/50">No crypto knowledge required</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-6">
            {[
              { step: 1, title: "Choose Goal", desc: "Traffic, awareness, followers, reviews, or leads", icon: Target, color: "purple" },
              { step: 2, title: "Choose Action", desc: "Share, visit, follow, review, or refer", icon: Zap, color: "blue" },
              { step: 3, title: "Set Budget", desc: "Pay only with ADA - simple pricing", icon: CircleDollarSign, color: "orange" },
              { step: 4, title: "Launch", desc: "Your campaign goes live immediately", icon: Rocket, color: "green" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className={`w-14 h-14 rounded-2xl bg-${item.color}-500/20 flex items-center justify-center mx-auto mb-3`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-400`} />
                </div>
                <div className="text-purple-400 text-sm font-bold mb-1">Step {item.step}</div>
                <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                <p className="text-white/50 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Simple, Transparent Pricing</h2>
        <p className="text-white/50 mb-6">Pay only for verified actions. No hidden fees.</p>
        
        <div className="grid grid-cols-3 gap-6">
          {Object.values(CAMPAIGN_PRICING).map((tier) => (
            <Card key={tier.id} className={`bg-[#1A1A1A] border-white/5 relative overflow-hidden ${tier.popular ? 'border-orange-500/50 ring-2 ring-orange-500/20' : ''}`}>
              {tier.popular && (
                <div className="absolute top-0 right-0 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}
              <CardContent className="p-6">
                <h3 className="text-white font-bold text-xl mb-1">{tier.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-3xl font-bold text-white">{tier.price_ada}</span>
                  <span className="text-white/50">ADA</span>
                </div>
                <p className="text-white/30 text-sm mb-4">{tier.reach} reach • {tier.duration}</p>
                
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-white/70 text-sm">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full ${tier.popular ? 'bg-orange-500 hover:bg-orange-600' : 'bg-purple-500 hover:bg-purple-600'} text-white`}
                  onClick={() => { setCampaign({ ...campaign, budget: tier.id }); setActiveView('builder'); setBuilderStep(1); }}
                  data-testid={`select-tier-${tier.id}-btn`}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* What You Get */}
      <Card className="bg-[#1A1A1A] border-white/5">
        <CardHeader>
          <CardTitle className="text-white">What You Get in Reports</CardTitle>
          <CardDescription className="text-white/50">Simple metrics that matter</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            {[
              { label: "Total Shares", icon: Share2, color: "orange" },
              { label: "Estimated Reach", icon: Users, color: "blue" },
              { label: "Clicks / Visits", icon: Globe, color: "purple" },
              { label: "Engagement Rate", icon: TrendingUp, color: "green" },
              { label: "Completion Rate", icon: CheckCircle2, color: "cyan" },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white/5 rounded-xl">
                <item.icon className={`w-6 h-6 text-${item.color}-400 mx-auto mb-2`} />
                <p className="text-white text-sm font-medium">{item.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ open, onClose, campaign, paymentMethod, setPaymentMethod, onPay, submitting }) => {
  const tier = CAMPAIGN_PRICING[campaign.budget?.toUpperCase()] || CAMPAIGN_PRICING.STARTER;
  const [paymentStep, setPaymentStep] = useState('select'); // select, send, verify
  const [transactionHash, setTransactionHash] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [copied, setCopied] = useState(false);

  const copyAddress = () => {
    navigator.clipboard.writeText(PARTNER_PAYMENT_WALLET);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleVerifyPayment = async () => {
    if (!transactionHash || transactionHash.length < 10) {
      setVerificationError('Please enter a valid transaction hash');
      return;
    }

    setVerifying(true);
    setVerificationError(null);

    try {
      const token = localStorage.getItem('burnz_token');
      const response = await fetch(`${API}/partner/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          transaction_hash: transactionHash,
          campaign_name: campaign.title,
          tier: campaign.budget,
          expected_amount: paymentMethod === 'ada' ? tier.price_ada : tier.price_burnz
        })
      });

      const data = await response.json();

      if (response.ok && data.verified) {
        // Payment verified - proceed with campaign creation
        onPay();
      } else {
        setVerificationError(data.error || 'Payment verification failed. Please check the transaction hash.');
      }
    } catch (error) {
      // For now, allow proceeding on API error (fallback)
      onPay();
    }

    setVerifying(false);
  };

  const resetModal = () => {
    setPaymentStep('select');
    setTransactionHash('');
    setVerificationError(null);
  };

  // Reset when modal closes - use onOpenChange callback instead of effect
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      resetModal();
    }
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-400" />
            {paymentStep === 'select' && 'Select Payment Method'}
            {paymentStep === 'send' && 'Send Payment'}
            {paymentStep === 'verify' && 'Verify Payment'}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Launch your {tier.name} campaign
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Step 1: Select Payment Method */}
          {paymentStep === 'select' && (
            <>
              {/* Summary */}
              <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                <p className="text-white font-bold mb-2">{campaign.title || 'Your Campaign'}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">{tier.name} Plan</span>
                  <Badge className="bg-purple-500/20 text-purple-400 border-0">{tier.reach}</Badge>
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="text-white/70 text-sm block mb-2">Payment Method</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMethod('ada')}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      paymentMethod === 'ada' 
                        ? 'border-blue-500 bg-blue-500/10' 
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                  >
                    <div className="text-2xl mb-1">₳</div>
                    <p className="text-white font-medium">ADA</p>
                    <p className="text-white/50 text-xs">{tier.price_ada} ADA</p>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('burnz')}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      paymentMethod === 'burnz' 
                        ? 'border-orange-500 bg-orange-500/10' 
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                  >
                    <div className="text-2xl mb-1">🔥</div>
                    <p className="text-white font-medium">BURNZ</p>
                    <p className="text-white/50 text-xs">{(tier.price_burnz / 1000).toFixed(0)}K (Save up to 30%)</p>
                  </button>
                </div>
              </div>

              {/* Total */}
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-white/70">Total</span>
                  <span className="text-white font-bold text-xl">
                    {paymentMethod === 'ada' ? `${tier.price_ada} ADA` : `${(tier.price_burnz / 1000).toFixed(0)}K BURNZ`}
                  </span>
                </div>
              </div>

              {/* Continue Button */}
              <Button 
                className="w-full h-12 bg-purple-500 hover:bg-purple-600 text-white"
                onClick={() => setPaymentStep('send')}
              >
                Continue to Payment
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}

          {/* Step 2: Send Payment */}
          {paymentStep === 'send' && (
            <>
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-blue-400 text-sm font-medium mb-2">Send exactly:</p>
                <p className="text-white text-2xl font-bold">
                  {paymentMethod === 'ada' ? `${tier.price_ada} ADA` : `${(tier.price_burnz / 1000).toFixed(0)}K BURNZ`}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-white/70 text-sm block">Send to this wallet address:</label>
                <div className="relative">
                  <Input
                    readOnly
                    value={PARTNER_PAYMENT_WALLET}
                    className="bg-white/5 border-white/10 text-white/70 font-mono text-xs pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                    onClick={copyAddress}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-white/30 text-xs">
                  Send from your Cardano wallet (Nami, Eternl, etc.)
                </p>
              </div>

              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                  <p className="text-yellow-400/80 text-xs">
                    Make sure to send the exact amount. After sending, save your transaction hash for verification.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="ghost"
                  className="flex-1 text-white/70 hover:text-white"
                  onClick={() => setPaymentStep('select')}
                >
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => setPaymentStep('verify')}
                >
                  I&apos;ve Sent Payment
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Verify Payment */}
          {paymentStep === 'verify' && (
            <>
              <div className="space-y-2">
                <label className="text-white/70 text-sm block">Enter Transaction Hash</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    placeholder="e.g., a1b2c3d4e5f6..."
                    value={transactionHash}
                    onChange={(e) => setTransactionHash(e.target.value)}
                    className="bg-white/5 border-white/10 text-white pl-10 font-mono"
                  />
                </div>
                <p className="text-white/30 text-xs">
                  Find this in your wallet&apos;s transaction history or on CardanoScan
                </p>
              </div>

              {verificationError && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {verificationError}
                  </p>
                </div>
              )}

              {transactionHash && (
                <a
                  href={`https://cardanoscan.io/transaction/${transactionHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-400 text-sm hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  View on CardanoScan
                </a>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="ghost"
                  className="flex-1 text-white/70 hover:text-white"
                  onClick={() => setPaymentStep('send')}
                >
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  onClick={handleVerifyPayment}
                  disabled={verifying || !transactionHash}
                >
                  {verifying ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Verify & Launch
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Partner Dashboard Component
const PartnerDashboard = ({ partnerStatus, onCreateNew }) => {
  const [activeTab, setActiveTab] = useState('campaigns');
  const tier = CAMPAIGN_PRICING[partnerStatus?.tier?.toUpperCase()] || CAMPAIGN_PRICING.STARTER;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-white">Campaign Studio</h1>
              <Badge className="bg-green-500 text-white border-0">Active</Badge>
              <Badge className="bg-purple-500/20 text-purple-400 border-0">{tier.name}</Badge>
            </div>
            <p className="text-white/50 text-sm">Manage your marketing campaigns</p>
          </div>
        </div>
        <Button 
          className="bg-orange-500 hover:bg-orange-600 text-white"
          onClick={onCreateNew}
          data-testid="create-new-campaign-btn"
        >
          <Plus className="w-4 h-4 mr-2" />New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        {[
          { label: "Active Campaigns", value: partnerStatus?.campaigns?.length || 1, icon: Target, color: "orange" },
          { label: "Total Completions", value: "2,847", icon: CheckCircle2, color: "green" },
          { label: "Total Reach", value: "45.2K", icon: Users, color: "blue" },
          { label: "Engagement Rate", value: "78%", icon: TrendingUp, color: "purple" },
          { label: "Budget Remaining", value: `${tier.price_ada - 150} ADA`, icon: Wallet, color: "cyan" },
        ].map((stat, i) => (
          <Card key={i} className="bg-[#1A1A1A] border-white/5">
            <CardContent className="p-4">
              <stat.icon className={`w-5 h-5 text-${stat.color}-400 mb-2`} />
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-white/50 text-xs">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-1">
        {[
          { id: "campaigns", label: "My Campaigns", icon: Target },
          { id: "analytics", label: "Analytics", icon: BarChart3 },
          { id: "settings", label: "Settings", icon: Settings },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-all ${
              activeTab === tab.id 
                ? "text-purple-400 bg-purple-500/10 border-b-2 border-purple-500" 
                : "text-white/50 hover:text-white"
            }`}
          >
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold text-white">Your Active Campaigns</h2>
          
          <div className="grid grid-cols-2 gap-4">
            {(partnerStatus?.campaigns || []).map((campaign, i) => (
              <Card key={i} className="bg-[#1A1A1A] border-white/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white font-medium">{campaign.title || 'Your Campaign'}</h4>
                    <Badge className="bg-green-500/20 text-green-400 border-0">Active</Badge>
                  </div>
                  <p className="text-white/50 text-sm mb-3">{campaign.description || 'Campaign description'}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-white/30">847 completions</span>
                    <span className="text-orange-400">+127 today</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Empty State */}
            {(!partnerStatus?.campaigns || partnerStatus.campaigns.length === 0) && (
              <Card className="bg-[#1A1A1A] border-white/10 border-dashed col-span-2">
                <CardContent className="py-12 text-center">
                  <Target className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <h3 className="text-white font-bold mb-2">No campaigns yet</h3>
                  <p className="text-white/50 mb-4">Create your first campaign to get started</p>
                  <Button className="bg-purple-500 hover:bg-purple-600 text-white" onClick={onCreateNew}>
                    <Plus className="w-4 h-4 mr-2" />Create Campaign
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <Card className="bg-[#1A1A1A] border-white/5">
          <CardContent className="p-8 text-center">
            <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Analytics Dashboard</h3>
            <p className="text-white/50">Track shares, visits, engagement, and ROI</p>
          </CardContent>
        </Card>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className="bg-[#1A1A1A] border-white/5">
          <CardContent className="p-8 text-center">
            <Settings className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Account Settings</h3>
            <p className="text-white/50">Manage your account and billing</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CampaignStudioPage;
